import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { formatPrice } from '@components/ProductOffers/modals/ShoppingCartDrawer/utils/cartItemCard.utils';
import { ChannelLogoTooltip } from '@components/ProductOffers/misc/ChannelLogoTooltip';
import { TVChannelLogos } from '@components/ProductOffers/misc/TVChannelLogos';
import type { CartAddOnsContentProps } from '@components/ProductOffers/modals/ShoppingCartDrawer/CartItemCard/CartItemCard.types';
import { ExpandableSectionContainer } from '@components/ProductOffers/modals/ShoppingCartDrawer/ShoppingCartDrawer.styles';
import { DiscountInfo } from '@shared/components/display/DiscountInfo/DiscountInfo';
import { PromoPriceDisplay } from '@shared/components/display/PromoPriceDisplay/PromoPriceDisplay';
import { QuantityCounter } from '@shared/components/inputs/QuantityCounter/QuantityCounter';

const CartAddOnsContent = ({
  themePackages,
  individualChannels,
  serviceAddOns,
  onUpdateChildQuantity,
}: CartAddOnsContentProps) => {
  const theme = useTheme();

  return (
    <ExpandableSectionContainer>
      {/* Theme Packages */}
      {themePackages.map((pkg, i) => (
        <Paper
          key={`theme-${i}`}
          variant="cartItemSurface"
          sx={{ p: '16px', mb: i < themePackages.length - 1 ? '12px' : 0 }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2.5} useFlexGap>
            {/* Name + label */}
            <Stack spacing={1} useFlexGap>
              <Typography variant="label.button" color={theme.colors.black}>
                {pkg.name}
              </Typography>
              <Typography variant="subtitle.card" color={theme.colors.black}>
                Theme Package
              </Typography>
            </Stack>
            {/* Price + discount: right-aligned beside the name on tablet+, left-aligned beneath it when stacked */}
            <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }} spacing={0.5} useFlexGap>
              <PromoPriceDisplay regularPrice={pkg.price} color={theme.colors.black} />
              <DiscountInfo promo={pkg.promo} discountTag={pkg.discountTag} discountMessage={pkg.discountMessage} />
            </Stack>
          </Stack>
          {pkg.channels.length > 0 && (
            <Box sx={{ mt: '15px' }}>
              <TVChannelLogos
                channels={pkg.channels}
                maxVisible={Infinity}
                tooltipPlacement="top"
                logoWidth={103}
                logoHeight={78}
              />
            </Box>
          )}
        </Paper>
      ))}

      {/* Individual Channels */}
      {individualChannels.map((ch, i) => {
        const channel = {
          name: ch.name,
          logo: ch.imageUrl,
          description: ch.description,
          descriptionImage: ch.descriptionImage ?? ch.imageUrl,
        };
        return (
          <Paper
            key={channel.name}
            variant="cartItemSurface"
            sx={{
              p: '15px',
              mb: i < individualChannels.length - 1 ? '12px' : 0,
              mt: themePackages.length > 0 && i === 0 ? '12px' : 0,
            }}
          >
            {/* Logo beside the details on tablet+, logo-on-top stacked on mobile so the
                name and inline discount get full width instead of crushing in a narrow column. */}
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'stretch', md: 'center' }}
              spacing={1.5}
              useFlexGap
              sx={{ py: '10px' }}
            >
              <ChannelLogoTooltip channel={channel} placement="top" logoWidth={105} logoHeight={80} />
              <Stack spacing={0} sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="label.button" color={theme.colors.black}>
                  {ch.name}
                </Typography>
                <Typography variant="subtitle.card" color={theme.colors.black}>
                  Individual Channel
                </Typography>
                <DiscountInfo promo={ch.promo} layout="inline" />
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1} useFlexGap sx={{ flexShrink: 0 }}>
                <PromoPriceDisplay regularPrice={ch.price} color={theme.colors.black} />
              </Stack>
            </Stack>
          </Paper>
        );
      })}

      {/* Service Add-Ons */}
      {serviceAddOns.map((addOn, i) => (
        <Stack
          key={addOn.offeringId}
          component={Paper}
          variant="cartItemSurface"
          direction={{ xs: 'column', md: 'row' }}
          alignItems="flex-start"
          spacing={1.25}
          useFlexGap
          sx={{ p: '15px', mb: i < serviceAddOns.length - 1 ? '12px' : 0 }}
        >
          {/* Name + Description */}
          <Stack spacing={0.5} useFlexGap sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="label.button" color={theme.colors.black}>
              {addOn.name}
            </Typography>
            {addOn.description && (
              <Typography variant="body.sm" sx={{ color: theme.colors.black, lineHeight: 1.3 }}>
                {addOn.description}
              </Typography>
            )}
          </Stack>

          {/* Price + Discount + Quantity: right-aligned beside the name on tablet+, left-aligned beneath it when stacked */}
          <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }} spacing={0.5} useFlexGap flexShrink={0}>
            <PromoPriceDisplay regularPrice={addOn.price} color={theme.colors.black} />
            <DiscountInfo promo={addOn.promo} discountTag={addOn.discountTag} discountMessage={addOn.discountMessage} />

            {addOn.supportsQuantity && addOn.price != null && addOn.price > 0 && onUpdateChildQuantity && (
              <Stack alignItems={{ xs: 'flex-start', md: 'flex-end' }} useFlexGap sx={{ gap: '5px', mt: '6px' }}>
                <Stack direction="row" alignItems="center" spacing={1.25} useFlexGap>
                  <Typography variant="label.fieldSmall" color={theme.colors.primary}>
                    Quantity:
                  </Typography>
                  <QuantityCounter
                    value={addOn.quantity}
                    min={1}
                    max={10}
                    onChange={(newQty) =>
                      onUpdateChildQuantity(addOn.childItemId, addOn.offeringId, addOn.name, newQty)
                    }
                  />
                </Stack>
                <Typography variant="body.sm" sx={{ color: theme.colors.black, lineHeight: 1.3 }}>
                  {`Each quantity adds ${formatPrice(addOn.price)} / month`}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      ))}

      {/* Empty state */}
      {themePackages.length === 0 && individualChannels.length === 0 && serviceAddOns.length === 0 && (
        <Typography variant="body.sm" color={theme.novus.mutedText}>
          No add-ons
        </Typography>
      )}
    </ExpandableSectionContainer>
  );
};

export default CartAddOnsContent;
